import type { CartItem } from '../context/CartContext';
import { resolveDiscountPricing, type DiscountablePrice } from './pricing';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeUnitPrice(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return Number(parsed.toFixed(2));
}

function collectPriceCandidates(value: unknown) {
  if (!isRecord(value)) {
    return [];
  }

  const nestedRecords = [
    value.item,
    value.data,
    value.cart_item,
    value.cartItem,
    value.resolved_specs,
    value.resolvedSpecs,
    value.catalog_item,
    value.catalogItem,
  ].filter(isRecord);

  return [
    value.unit_price,
    value.unitPrice,
    value.base_price,
    value.basePrice,
    value.price,
    ...nestedRecords.flatMap((record) => [
      record.unit_price,
      record.unitPrice,
      record.base_price,
      record.basePrice,
      record.price,
    ]),
  ];
}

export function extractRemoteUnitPrice(value: unknown) {
  for (const candidate of collectPriceCandidates(value)) {
    const normalized = normalizeUnitPrice(candidate);
    if (normalized !== null) {
      return normalized;
    }
  }

  return null;
}

export function resolveCatalogProductUnitPrice(product?: DiscountablePrice | null) {
  if (!product) {
    return null;
  }

  const pricing = resolveDiscountPricing(product);
  return pricing.price > 0 ? pricing.price : null;
}

// The live catalog price is the source of truth so a cart reflects current
// pricing on reload. The stored price is only a fallback for items the catalog
// cannot resolve.
export function resolveCartItemUnitPrice(
  item: Pick<CartItem, 'price' | 'itemId'>,
  catalogProduct?: DiscountablePrice | null,
  extraCandidates: unknown[] = [],
) {
  const catalogPrice = resolveCatalogProductUnitPrice(catalogProduct);
  if (catalogPrice !== null) {
    return catalogPrice;
  }

  for (const candidate of extraCandidates) {
    const extractedPrice = extractRemoteUnitPrice(candidate);
    if (extractedPrice !== null) {
      return extractedPrice;
    }
  }

  return normalizeUnitPrice(item.price) ?? 0;
}
